import locator from '@/service-locator'
import config from '@/config'
import { loadRemoteScript } from '@/utils/loader'

const ZENDESK_EMBED_URL = 'https://static.zdassets.com/ekr/snippet.js'

const SUPPORTED_ZENDESK_BOT_LANG = ['en', 'ar']

const MAPPING_ZENDESK_LOCALE = {
  en: 'en-US',
  ar: 'ar-SA',
}

const ZENDESK_META_FIELDS = {
  bot_locale: config.zendeskMetaBotLocaleMobileWeb,
  contact_platform: config.zendeskMetaContactPlatform,
  merchant_authenticated_status: config.zendeskMetaMerchantAuthenticatedStatus,
  merchant_name: config.zendeskMetaMerchantName,
  merchant_tier: config.zendeskMetaMerchantTier,
  merchant_email: config.zendeskMetaMerchantEmail,
  merchant_id: config.zendeskMetaMerchantId,
  merchant_code: config.zendeskMetaMerchantCode,
}

class ZendeskChatbotService {
  constructor() {
    this.metaFields = {}
  }

  isAvailable = () => {
    return !!locator.confiig.zendeskKey
  }

  /**
   * @param targetLanguage
   * @returns {boolean}
   */
  changeChatBotLanguage = (targetLanguage) => {
    if (!SUPPORTED_ZENDESK_BOT_LANG.includes(targetLanguage)) {
      console.warn(
        `Zendesk: Set 'language' was failed. We don't support [${targetLanguage}] for Zendesk.`
      )

      return false
    }

    if (!this.metaFields) {
      this.metaFields = {}
    }

    if (window.zE) {
      const targetLocale = MAPPING_ZENDESK_LOCALE[targetLanguage]

      window.zE('messenger:set', 'locale', targetLocale)

      this.metaFields.locale = targetLocale

      return true
    }

    console.warn('Zendesk is not available! Please try again later.')

    return false
  }

  hideChatbot = async () => {
    if (!window.zE) {
      console.warn('Zendesk is not available!')

      return
    }

    // We call stop directly. If Zendesk is not available, it will throw an error. Ignore it and we don't need to anything!
    try {
      window.zE('messenger', 'hide')
    } catch (error) {
      console.warn(error.message)
    }
  }

  showChatbot = async () => {
    if (!window.zE) {
      console.warn('Zendesk is not available!')

      return
    }

    window.zE('messenger', 'show')
  }

  openChatbot = () => {
    if (!window.zE) {
      console.warn('Zendesk is not available!')

      return
    }

    window.zE('messenger', 'open')
  }

  closeChatbot = () => {
    if (!window.zE) {
      console.warn('Zendesk is not available!')

      return
    }

    window.zE('messenger', 'close')
  }

  setChatMetaFields = (newFields) => {
    if (!this.metaFields) {
      this.metaFields = {}
    }

    const locale =
      MAPPING_ZENDESK_LOCALE[newFields?.language] ||
      this.metaFields.locale ||
      'en-US'

    const inputMerchantTier =
      newFields?.merchant_tier?.tier || this.metaFields?.merchant_email || ''

    const merchantTier =
      {
        'Tier 0': 't0',
        'Tier 1': 't1',
        'Tier 2': 't2',
        'Tier 3': 't3',
        T0: 't0',
        T1: 't1',
        T2: 't2',
        T3: 't3',
        'Tier 3 - Unverified Pilot': 't3',
      }[inputMerchantTier] || 'na'

    const authStatus =
      newFields?.merchant_authenticated_status ??
      this.metaFields.merchant_authenticated_status ??
      false

    this.metaFields = {
      locale: locale,
      merchant_authenticated_status: authStatus,
      merchant_name:
        newFields?.merchant_name || this.metaFields?.merchant_name || '',
      merchant_id: newFields?.merchant_id || this.metaFields?.merchant_id || '',
      merchant_email:
        newFields?.merchant_email || this.metaFields?.merchant_email || '',
      merchant_tier: merchantTier,
      merchant_code:
        newFields?.merchant_code || this.metaFields?.merchant_code || '',
      contact_platform: 'tamara_partners_portal_web',
    }

    if (window.zE) {
      window.zE('messenger:set', 'conversationFields', [
        {
          id: ZENDESK_META_FIELDS.bot_locale,
          value: this.metaFields.locale,
        },
        {
          id: ZENDESK_META_FIELDS.contact_platform,
          value: this.metaFields.contact_platform,
        },
        {
          id: ZENDESK_META_FIELDS.merchant_name,
          value: this.metaFields.merchant_name,
        },
        {
          id: ZENDESK_META_FIELDS.merchant_tier,
          value: this.metaFields.merchant_tier,
        },
        {
          id: ZENDESK_META_FIELDS.merchant_email,
          value: this.metaFields.merchant_email,
        },
        {
          id: ZENDESK_META_FIELDS.merchant_id,
          value: this.metaFields.merchant_id,
        },
        {
          id: ZENDESK_META_FIELDS.merchant_code,
          value: this.metaFields.merchant_code,
        },
        {
          id: ZENDESK_META_FIELDS.merchant_authenticated_status,
          value: `${this.metaFields.merchant_authenticated_status}`,
        },
      ])
    }
  }

  startChatbot = async (settings) => {
    if (window.zE || !locator.config.zendeskKey) {
      return
    }

    const srcUrl = `${ZENDESK_EMBED_URL}?key=${locator.config.zendeskKey}`

    // It will start after loaded
    await loadRemoteScript(srcUrl, {
      cancelOnTimeout: true,
      attributes: {
        id: 'ze-snippet',
      },
      onSuccess: () => {
        const { language, metaFields, sensitiveMetaFields } = settings || {}

        this.closeChatbot()

        this.changeChatBotLanguage(language)
        this.setChatMetaFields({
          language,
          ...metaFields,
          ...sensitiveMetaFields,
        })

        setTimeout(() => {
          locator.store.dispatch('chatbot/setZendeskScriptLoadedStatus', true)
        }, 2000)
      },
    })
  }

  /**
   *
   * @param language
   * @param metaFields
   * @param sensitiveMetaFields
   */
  initChatBot = ({ language, metaFields, sensitiveMetaFields }) => {
    this.startChatbot({ language, metaFields, sensitiveMetaFields })
  }
}

export default ZendeskChatbotService
